import React, { useCallback } from 'react';
import { isSameDay } from 'date-fns';
import { Container, Typography, Box, Chip, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { translate } from 'translations/translator';
import { HwtButton } from 'components/@common/Buttons/HwtButton';
import { updateInstructionDates } from 'state/modules/entity/thunks/teaching-order';
import { useDispatch, useSelector } from 'react-redux';
import { composeInstructionDate } from 'util/lesson-plan-util';
import { AppStoreState } from 'types';
import { MultiDayDatePicker } from 'components/@common/Inputs/MultiDayDatePicker';
import { formatUiDate } from 'util/date-util';
import LpVariant from 'translations/variants/lessonPlanner';

const getTranslation = (key: LpVariant) => translate('lessonPlanner', key);

export const ManageNonTeachingDays: React.FC = () => {
  const dispatch = useDispatch() as any;
  const theme = useTheme();
  const classes = useStyles(theme)();
  const [selectedDates, setSelectedDates] = React.useState<any[]>([]);

  const nonTeachingDays: any = useSelector(
    ({ entity }: AppStoreState) => entity.productConfig?.skipDates
  );

  const handleRemoveNonTeachingDay = useCallback(
    date => async () => {
      await dispatch(updateInstructionDates(composeInstructionDate(date, false)));
      const idx = nonTeachingDays
        .map(date => new Date(date))
        .findIndex(day => isSameDay(day, date));
      nonTeachingDays.splice(idx, 1);
    },
    [dispatch, nonTeachingDays]
  );

  const getNonTeachingDays = useCallback(() => {
    return nonTeachingDays
      .map(date => new Date(date))
      .sort((a, b) => a.getTime() - b.getTime())
      .map((date, idx) => ({
        id: idx,
        date
      }));
  }, [nonTeachingDays]);

  const handleCalendarChange = (_, dates: string[]) => setSelectedDates(dates);

  const onAddNonTeachingDay = () => {
    const instructionDates = selectedDates.map(date => composeInstructionDate(date, true));
    dispatch(updateInstructionDates(instructionDates));
    setSelectedDates([]);
  };

  return (
    <Container>
      <Typography variant="h4" style={{ marginTop: 32, marginBottom: 10, fontWeight: 400 }}>
        {getTranslation(LpVariant.addNonTeachingDays)}
      </Typography>
      <Box style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
        <Typography variant="h6" style={{ fontWeight: 300 }}>
          {getTranslation(LpVariant.addNonTeachingDayToLessonPlan)}
        </Typography>
        <Box className={classes.calendarAction}>
          <MultiDayDatePicker
            sx={{ marginRight: '25px', width: '485px', border: '2px solid' }}
            label={getTranslation(LpVariant.nonTeachingDay)}
            shouldDisableWeekends
            // labelFunc={dates =>
            //   dates?.length === 0
            //     ? getTranslation(LpVariant.pleaseSelectNonTeachingDays)
            //     : undefined
            // }
            value={selectedDates}
            onChange={handleCalendarChange}
            SubmitChildComponent={
              <HwtButton
                onClick={onAddNonTeachingDay}
                className={classes.button}
                variant="contained"
              >
                {getTranslation(LpVariant.saveChanges)}
              </HwtButton>
            }
          ></MultiDayDatePicker>
        </Box>
        <Box>
          {nonTeachingDays?.length > 0 &&
            getNonTeachingDays().map(({ date }, idx) => (
              <Chip
                key={`skip-date-${idx}`}
                className={classes.chip}
                label={formatUiDate(date)}
                deleteIcon={<FontAwesomeIcon icon={faTimes} className={classes.icon} />}
                onDelete={handleRemoveNonTeachingDay(date)}
              />
            ))}
        </Box>
      </Box>
    </Container>
  );
};

const useStyles = theme =>
  makeStyles(() => ({
    calendarAction: {
      display: 'flex',
      alignItems: 'flex-end',
      marginBottom: '25px',
      height: '97px'
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: '22px',
      padding: '24px',
      height: '445px',
      maxWidth: '1256px'
    },
    button: {
      margin: '8px 25px 2px 25px !important',
      height: '53px !important',
      width: 'max-content !important'
    },
    tableHeader: {
      display: 'flex',
      flex: 0.51,
      justifyContent: 'space-between',
      margin: '5px 0px'
    },
    table: {
      maxHeight: '261px',
      overflow: 'overlay'
    },
    rowActionIconButton: {
      width: '100px',
      height: '37px',
      textTransform: 'none'
    },
    chip: () => ({
      backgroundColor: `${theme.palette.link.main} !important`,
      width: 'fit-content',
      color: '#fff !important',
      height: '22px !important',
      fontWeight: 400,
      fontSize: '13px !important',
      margin: '3px 10px 10px 0 !important',
      padding: '0 !important',
      '& .MuiChip-deleteIcon': {
        color: '#fff !important',
        marginRight: '10px !important'
      }
    }),
    icon: {
      fontSize: '14px !important',
      '&:hover': {
        fontSize: '15px !important',
        cursor: 'pointer'
      }
    }
  }));
